// A registry of localStorage key PREFIXES that hold per-identity crash-recovery
// buffers — draft snapshots, unacked op logs — which must never survive a
// sign-out into the next person's session.
//
// ── WHY A REGISTRY AND NOT `lib/auth.tsx` IMPORTING THE BUFFER DIRECTLY ────
// `logout()` lives in core platform code; the buffers it must sweep are owned
// by feature modules (`modules/capture/lib/opBuffer.ts` today, and story
// 5-18's "unified draft recovery across capture/template/report/chat" will add
// more). `lib/` importing a module's internals would be exactly the
// cross-module coupling DOD §1.4 forbids, just aimed the other way — and it
// would break the "delete the module folder and the platform still compiles"
// test (P18 §1) for auth. A module registers its prefix here at import time;
// `lib/auth.tsx` only ever depends on this file, never on the module.
//
// It is deliberately a PREFIX registry, not a callback registry: the buffers
// are always `localStorage`, always namespaced `${prefix}:${orgId}:...`, and a
// prefix match is enough to sweep every namespace a module has ever written —
// including ones for identities the module has never seen this session.
const prefixes = new Set<string>();

/** Called once, at module import time, by whatever owns a recovery buffer. */
export function registerRecoveryBufferPrefix(prefix: string): void {
  prefixes.add(prefix);
}

/**
 * P05 §9.4's "cleared on … logout" (and every future recovery buffer 5-18
 * consolidates under it). Called from `lib/auth.tsx#logout`, beside the token
 * and the query cache: signing out must not leave one person's unsaved work in
 * a browser the next person signs into.
 */
export function clearAllRecoveryBuffers(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && [...prefixes].some((prefix) => key.startsWith(`${prefix}:`))) doomed.push(key);
    }
    doomed.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* nothing to clear if the store cannot be read */
  }
}
